import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { useAttachCallMutation } from "services/dialstack";
import type { Patient } from "services/types/patients.type";

import { selectAttachmentOverlay } from "../call-attachments.slice";
import type { IncomingCallPop } from "../incoming-call-pop.slice";
import useIncomingCallPatients from "../use-incoming-call-patients.hook";
import { callCardSx } from "./dialstack-softphone-widget.styles";
import SoftphonePatientHero from "./softphone-patient-hero.component";
import SoftphonePatientRow from "./softphone-patient-row.component";
import useInboundCallPops, { isRinging } from "./use-inbound-call-pops.hook";
import useTransferStatus from "./use-transfer-status.hook";

const MAX_VISIBLE_PATIENTS = 3;

const MATCHED_ON_CAPTIONS: Record<string, string> = {
  secondary_phone: "Matched on secondary number",
  responsible_party_phone: "Matched on responsible party's number"
};

// Exported for the on-handset workbench (ENG-2562), which has a pop but no
// SDK call for `IncomingCallPatientCard` to join it to.
export function PopPatients({
  pop,
  backendCallId,
  ringing,
  heroActions
}: {
  pop: IncomingCallPop;
  backendCallId: number | null;
  ringing: boolean;
  heroActions: "row" | "grid";
}) {
  const dispatch = useAppDispatch();
  const { patients, isLoading } = useIncomingCallPatients(pop.patientIds);
  const [attachCall, { isLoading: isAttaching }] = useAttachCallMutation();
  const overlay = useAppSelector(selectAttachmentOverlay);
  const attachedPatientId =
    backendCallId !== null
      ? (overlay[backendCallId]?.patient?.id ?? null)
      : null;

  if (isLoading) {
    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="45%" />
      </Stack>
    );
  }
  if (patients.length === 0) return null;

  const matchedOnCaption = pop.matchedOn
    ? MATCHED_ON_CAPTIONS[pop.matchedOn]
    : undefined;

  // Once staff picked which of the shared-number patients is on the line, the
  // call is attached and that patient is the only one the card names.
  const attachedPatient = patients.find(
    (patient) => patient.id === attachedPatientId
  );
  const soloPatient =
    attachedPatient ?? (patients.length === 1 ? patients[0] : undefined);
  const caption = attachedPatient ? "Attached to this call" : matchedOnCaption;

  // One patient to name gets the hero; shared numbers fall back to the rows.
  if (soloPatient) {
    return (
      <Box data-testid="incoming-call-patient-match">
        <SoftphonePatientHero
          patient={soloPatient}
          caption={caption}
          ringing={ringing}
          actions={heroActions}
        />
      </Box>
    );
  }

  // Several patients share the number: "This caller" attaches the call to the
  // one actually on the line, so its recording and summary reach the right
  // chart (no relationship, no number to remember — they already have it).
  const attachTo =
    backendCallId === null
      ? undefined
      : async (patient: Patient) => {
          const response = await attachCall({
            callId: backendCallId,
            patient_id: patient.id
          });
          dispatch(
            setCustomMessage(
              "error" in response ? (
                <ErrorSnackbarContent response={response} />
              ) : (
                "Call attached to the patient"
              )
            )
          );
          dispatch(setOpen(true));
        };

  const listed = attachedPatient ? [attachedPatient] : patients;
  const visible = listed.slice(0, MAX_VISIBLE_PATIENTS);
  const hiddenCount = listed.length - visible.length;

  return (
    <Box data-testid="incoming-call-patient-match">
      {visible.map((patient) => (
        <SoftphonePatientRow
          key={patient.id}
          patient={patient}
          onAttach={listed.length > 1 ? attachTo : undefined}
          isAttaching={isAttaching}
        />
      ))}
      {hiddenCount > 0 && (
        <Typography sx={{ fontSize: 12, color: neutral[800] }}>
          +{hiddenCount} more patient{hiddenCount > 1 ? "s" : ""} share this
          number
        </Typography>
      )}
      {caption && (
        <Typography
          sx={{ fontSize: 12, color: neutral[800], fontStyle: "italic" }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
}

// Patient context for inbound calls the backend matched to at least one
// patient. The join between the SDK call and the websocket pop lives in
// useInboundCallPops; unmatched callers get their own card.
//
// One card per call, inside the pop or the workbench for that call
// (ENG-2226): `callIds` names it. The pop's header already says which call
// this is and the number it came from, so the card carries only the patient
// — the hero for one match, rows for a shared number — plus who transferred
// it and the transfer status.
export default function IncomingCallPatientCard({
  callIds,
  heroActions = "row"
}: {
  callIds?: string[];
  // The workbench wants the 2×2 action grid with Take payment.
  heroActions?: "row" | "grid";
} = {}) {
  const inbound = useInboundCallPops();
  const matched = inbound.filter(
    (match) =>
      match.pop &&
      match.pop.patientIds.length > 0 &&
      (callIds === undefined || callIds.includes(match.callId))
  );
  const transferStatus = useTransferStatus();

  return (
    <>
      {matched.map(({ call, callId, pop, backendCallId }) => (
        <Box key={callId} data-testid="incoming-call-card" sx={callCardSx}>
          {pop?.transfer && (
            <Typography
              data-testid="incoming-call-transferred-by"
              sx={{ fontSize: 12, color: neutral[800] }}
            >
              Transferred by {pop.transfer.byName ?? "a colleague"}
            </Typography>
          )}
          {transferStatus(callId) && (
            <Typography
              data-testid="call-transfer-status"
              sx={{ fontSize: 12, color: neutral[800] }}
            >
              {transferStatus(callId)}
            </Typography>
          )}
          <PopPatients
            pop={pop as IncomingCallPop}
            backendCallId={backendCallId}
            ringing={isRinging(call)}
            heroActions={heroActions}
          />
        </Box>
      ))}
    </>
  );
}
